<script lang="ts">
  import { X } from '@lucide/svelte';
  import { type Snippet, tick } from 'svelte';
  import { fa } from '../i18n/fa';
  import { cn } from '../lib/cn';

  /**
   * Details open beside the page on desktop and as a bottom sheet on mobile (ADR-016); modals are
   * only for short confirmations. A native <dialog> gives focus trapping, Escape and the top layer.
   * A click on the backdrop closes it only while nothing in it was typed or changed (employer request
   * 2026-10-07 #3): a stray click must not throw away a half-filled form (pilot feedback #7). Any
   * input/change event inside marks it dirty; a caller with unsaved state of its own passes dirty.
   * × and Escape always close it.
   * Escape and the phone's back button are the browser's close request: the browser closes the
   * dialog itself and `close` updates the state; the code does not close it a second time while
   * the browser is still handling the request (the «بیشتر» sheet after a back press, 2026-10-15).
   */
  let {
    open,
    onClose,
    title,
    subtitle,
    actions,
    children,
    dirty = false,
  }: {
    open: boolean;
    onClose: () => void;
    title: string;
    subtitle?: Snippet | string;
    /** Sticky footer (the main commands of the record). */
    actions?: Snippet;
    children?: Snippet;
    /** Unsaved work the panel cannot see as an input event; keeps a backdrop click from closing it. */
    dirty?: boolean;
  } = $props();
  // Each panel its own title id: two open panels must not share one accessible name.
  const titleId = $props.id();

  let dialog = $state<HTMLDialogElement>();
  let heading = $state<HTMLHeadingElement>();
  // Something inside was typed or changed since the panel opened.
  let touched = $state(false);
  // Leaving: the panel stays until its short way out has played, so nothing vanishes under the eye.
  let closing = $state(false);
  const LEAVE_MS = 170;
  const EDIT_CONTROLS = '[aria-pressed], [role=option], [role=checkbox], [role=radio], [role=switch], [data-panel-edit]';
  // The press began on the backdrop (not a text selection dragged out of the panel).
  let downOutside = false;
  const outside = (e: MouseEvent) => {
    if (e.target !== dialog || !dialog) return false;
    const box = dialog.firstElementChild?.getBoundingClientRect();
    return !box || e.clientX < box.left || e.clientX > box.right || e.clientY < box.top || e.clientY > box.bottom;
  };
  $effect(() => {
    if (!dialog) return;
    if (open && !dialog.open) {
      touched = false;
      dialog.showModal();
      // The browser focuses the first control, the ×: start on the title instead, unless the content took focus.
      void tick().then(() => {
        const close = dialog?.querySelector('[data-panel-close]');
        if (document.activeElement === close || document.activeElement === dialog) heading?.focus({ preventScroll: true });
      });
    }
    if (open) {
      closing = false;
      delete dialog.dataset.closing;
    }
    if (!open && dialog.open) {
      const leaving = dialog;
      const still = typeof matchMedia !== 'function' || matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (still) {
        leaving.close();
        return;
      }
      closing = true;
      leaving.dataset.closing = '';
      const timer = setTimeout(() => {
        closing = false;
        delete leaving.dataset.closing;
        leaving.close();
      }, LEAVE_MS);
      return () => clearTimeout(timer);
    }
  });
</script>

<dialog
  bind:this={dialog}
  onclose={() => {
    // A late `close` of an earlier closing must not shut the panel opened again since.
    if (open && !dialog?.open) onClose();
  }}
  oninput={() => (touched = true)}
  onchange={() => (touched = true)}
  onpointerdown={(e) => (downOutside = outside(e))}
  onclick={(e) => {
    // Picking an option or toggle changes the form without an input event.
    if (e.target instanceof Element && e.target !== dialog && e.target.closest(EDIT_CONTROLS)) touched = true;
    if (downOutside && outside(e) && !touched && !dirty) onClose();
    downOutside = false;
  }}
  aria-labelledby={titleId}
  aria-hidden={closing || undefined}
  class={cn(
    'm-0 max-h-none max-w-none overflow-clip border-0 bg-transparent p-0 text-text backdrop:bg-[var(--scrim)]',
    // mobile: bottom sheet; desktop: a drawer on the end side (left in RTL), clear of the nav rail
    'inset-x-0 bottom-0 top-auto w-full lg:inset-y-0 lg:start-auto lg:end-0 lg:h-dvh lg:w-[440px]',
  )}
>
  {#if open || closing}
    <div
      class={cn(
        'flex max-h-[88dvh] flex-col rounded-t-xl border border-[var(--neu-edge)] bg-surface shadow-raised-lg lg:h-full lg:max-h-none lg:rounded-e-none lg:rounded-s-xl lg:border-y-0 lg:border-e-0',
        closing ? 'sheet-leave' : 'sheet-enter',
      )}
    >
      <div class="mx-auto mt-2 h-1 w-10 rounded-full bg-border-strong lg:hidden" aria-hidden="true"></div>
      <header class="flex items-start justify-between gap-3 border-b border-hairline px-5 pb-3 pt-3 lg:pt-5">
        <div class="min-w-0">
          <h2 bind:this={heading} id={titleId} tabindex="-1" style="outline: none" class="truncate text-lg font-bold">{title}</h2>
          {#if subtitle}<div class="mt-0.5 text-sm text-text-3">{#if typeof subtitle === 'string'}{subtitle}{:else}{@render subtitle()}{/if}</div>{/if}
        </div>
        <button
          type="button"
          data-panel-close
          onclick={onClose}
          class="-me-2 flex size-11 shrink-0 items-center justify-center rounded-md text-text-3 transition-colors duration-[var(--dur-fast)] hover:bg-surface-3 active:shadow-sunken"
          aria-label={fa.app.close}
        >
          <X class="size-5" aria-hidden="true" />
        </button>
      </header>
      <div class="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-4">{@render children?.()}</div>
      {#if actions}
        <footer class="flex flex-wrap gap-2 border-t border-hairline px-5 py-3" style="padding-bottom: max(0.75rem, env(safe-area-inset-bottom))">
          {@render actions()}
        </footer>
      {/if}
    </div>
  {/if}
</dialog>
